// =====================================================
// SearchModal.tsx — design identique à profil.tsx
// expo-video / VideoView, SafeImage, LikeButton glow violet,
// ShareButton, stats bar cercle violet, thumbnail statique → MediaViewer
// =====================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  StyleSheet, Text, View, Modal, TouchableOpacity, TextInput,
  ScrollView, Image, Platform, ActivityIndicator, Keyboard,
  Dimensions, Animated,
} from 'react-native';
import { Ionicons }              from '@expo/vector-icons';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient }        from 'expo-linear-gradient';
import { VideoView, useVideoPlayer } from 'expo-video';
import * as Haptics              from 'expo-haptics';
import AsyncStorage              from '@react-native-async-storage/async-storage';
import { useTranslation }        from 'react-i18next';
import ShareButton               from '../components/ShareButton';

const { width, height } = Dimensions.get('window');
const BACKEND_URL = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const HOME_URL    = `${BACKEND_URL}/home`;
const NATIVE      = Platform.OS !== 'web';

const LIKE_ACTIVE_COLOR   = '#7B1FA2';
const LIKE_INACTIVE_COLOR = '#65676B';

type SearchCategory = 'all' | 'posts' | 'images' | 'videos' | 'music' | 'artisanat';

interface Post {
  id:          string;
  author_id:   string;
  author:      { nom: string; prenom: string; avatar_url: string | null };
  content:     string;
  visibility:  string;
  created_at:  string;
  imagepots?:  string | null;
  vidposts?:   string | null;
  reactions:   { likes: number; comments: number; shares: number };
  user_liked:  boolean;
  user_shared: boolean;
  user_saved:  boolean;
}

interface SubmissionMedia { id: string; url: string; description?: string | null }

interface Submission {
  id:          string;
  game_type:   'arts' | 'performance' | 'music' | 'artisanat';
  badge:       string;
  run_id:      string;
  session_id:  string;
  user_id:     string;
  run_number:  number;
  run_title:   string;
  run_status:  string;
  author:      { nom: string; prenom: string; avatar_url: string | null };
  media:       SubmissionMedia[];
  total_votes: number;
  user_voted:  boolean;
  created_at:  string;
}

type Result = ({ kind: 'post' } & Post) | ({ kind: 'sub' } & Submission);

interface SearchModalProps {
  visible:     boolean;
  onClose:     () => void;
  onPostPress: (postId: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtAgo = (d: string, t: (key: string, opts?: any) => string) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60)    return t('searchModal.time.now');
  if (s < 3600)  return t('searchModal.time.minutes', { count: Math.floor(s / 60) });
  if (s < 86400) return t('searchModal.time.hours', { count: Math.floor(s / 3600) });
  return t('searchModal.time.days', { count: Math.floor(s / 86400) });
};
const ini = (nom: string, p: string) => `${p[0]}${nom[0]}`.toUpperCase();

async function getValidToken(): Promise<{ uid: string; token: string; refresh_token: string; expires_at: number } | null> {
  try {
    const raw = await AsyncStorage.getItem('harmonia_session');
    if (!raw) return null;
    const session      = JSON.parse(raw);
    const uid          = session?.user?.id      || '';
    const accessToken  = session?.access_token  || '';
    const refreshToken = session?.refresh_token || '';
    const expiresAt    = session?.expires_at    || 0;
    if (!uid || !accessToken) return null;
    const now = Math.floor(Date.now() / 1000);
    if (expiresAt - now > 60) return { uid, token: accessToken, refresh_token: refreshToken, expires_at: expiresAt };
    if (!refreshToken) return null;
    const res = await fetch(`${BACKEND_URL}/refresh-token`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const data           = await res.json();
    const updatedSession = { ...session, access_token: data.access_token, refresh_token: data.refresh_token, expires_at: data.expires_at };
    await AsyncStorage.setItem('harmonia_session', JSON.stringify(updatedSession));
    return { uid, token: data.access_token, refresh_token: data.refresh_token, expires_at: data.expires_at };
  } catch { return null; }
}

function useCats(t: (key: string) => string): { key: SearchCategory; label: string; icon: string }[] {
  return [
    { key: 'all',       label: t('searchModal.categories.all'),       icon: 'apps-outline'          },
    { key: 'posts',     label: t('searchModal.categories.posts'),     icon: 'document-text-outline' },
    { key: 'images',    label: t('searchModal.categories.images'),    icon: 'images-outline'        },
    { key: 'videos',    label: t('searchModal.categories.videos'),    icon: 'videocam-outline'      },
    { key: 'music',     label: t('searchModal.categories.music'),     icon: 'musical-notes-outline' },
    { key: 'artisanat', label: t('searchModal.categories.artisanat'), icon: 'hammer-outline'        },
  ];
}

const GT_FOR_CAT: Record<SearchCategory, string | null> = {
  all: null, posts: null, images: 'arts', videos: 'performance', music: 'music', artisanat: 'artisanat',
};

// ─── Highlight ────────────────────────────────────────────────────────────────
function Hl({ text, q }: { text: string; q: string }) {
  if (!q.trim()) return <Text>{text}</Text>;
  const safe  = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${safe})`, 'gi'));
  return (
    <Text>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <Text key={i} style={S.hl}>{p}</Text>
          : <Text key={i}>{p}</Text>
      )}
    </Text>
  );
}

// ─── SafeImage ────────────────────────────────────────────────────────────────
function SafeImage({ uri, style, resizeMode = 'cover', fallback = null }: {
  uri: string | null | undefined; style: any;
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'center';
  fallback?: React.ReactNode;
}) {
  const [error, setError] = useState(false);
  useEffect(() => { setError(false); }, [uri]);
  if (!uri || error) return fallback ? <>{fallback}</> : null;
  return <Image source={{ uri }} style={style} resizeMode={resizeMode} onError={() => setError(true)} />;
}

// ─── MobileVideoPlayer ────────────────────────────────────────────────────────
function MobileVideoPlayer({ url }: { url: string }) {
  const player = useVideoPlayer({ uri: url }, p => { p.play(); });
  return (
    <VideoView player={player} style={S.mvNativeVideo} contentFit="contain" nativeControls />
  );
}

// ─── MediaViewer ─────────────────────────────────────────────────────────────
function MediaViewer({ visible, url, type, onClose }: {
  visible: boolean; url: string | null; type: 'image' | 'video'; onClose: () => void;
}) {
  if (!url) return null;
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <View style={S.mvOverlay}>
        <TouchableOpacity style={S.mvCloseBtn} onPress={onClose}>
          <Ionicons name="close" size={30} color="#fff" />
        </TouchableOpacity>
        {type === 'image' ? (
          <TouchableOpacity activeOpacity={1} onPress={onClose}>
            <SafeImage uri={url} style={S.mvImage} resizeMode="contain" />
          </TouchableOpacity>
        ) : Platform.OS === 'web' ? (
          // @ts-ignore
          <video src={url} controls autoPlay style={{ width: '100%', maxHeight: '90%', objectFit: 'contain', backgroundColor: '#000' }} />
        ) : (
          <MobileVideoPlayer url={url} />
        )}
      </View>
    </Modal>
  );
}

// ─── LikeButton — identique à profil.tsx ─────────────────────────────────────
function LikeButton({ liked, isAnim, onPress }: {
  liked: boolean; isAnim: boolean; onPress: () => void;
}) {
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const glowAnim  = useRef(new Animated.Value(0)).current;

  const handlePress = () => {
    if (isAnim) return;
    Animated.parallel([
      Animated.sequence([
        Animated.timing(scaleAnim, { toValue: 1.3, duration: 120, useNativeDriver: true }),
        Animated.spring(scaleAnim, { toValue: 1, friction: 4, tension: 50, useNativeDriver: true }),
      ]),
      Animated.sequence([
        Animated.timing(glowAnim, { toValue: 1, duration: 120, useNativeDriver: false }),
        Animated.timing(glowAnim, { toValue: 0, duration: 400, useNativeDriver: false }),
      ]),
    ]).start();
    onPress();
  };

  const glowColor = glowAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: ['rgba(123,31,162,0)', 'rgba(123,31,162,0.18)'],
  });

  return (
    <TouchableOpacity style={S.actionBtnModern} onPress={handlePress} disabled={isAnim} activeOpacity={0.7}>
      <Animated.View style={[
        S.actionContentModern,
        S.likeBtnInner,
        liked && S.likeBtnActive,
        { backgroundColor: glowColor as any },
      ]}>
        <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
          <MaterialCommunityIcons
            name={liked ? 'thumb-up' : 'thumb-up-outline'}
            size={26}
            color={liked ? LIKE_ACTIVE_COLOR : LIKE_INACTIVE_COLOR}
          />
        </Animated.View>
      </Animated.View>
    </TouchableOpacity>
  );
}

// ─── PostCard ─────────────────────────────────────────────────────────────────
function PostCard({ post, q, userId, onComment, t }: {
  post:      Post;
  q:         string;
  userId:    string;
  onComment: () => void;
  t:         (key: string, opts?: any) => string;
}) {
  const [liked,       setLiked]       = useState(post.user_liked);
  const [shared,      setShared]      = useState(post.user_shared);
  const [likesCount,  setLikesCount]  = useState(post.reactions.likes);
  const [sharesCount, setSharesCount] = useState(post.reactions.shares);
  const [isAnim,      setIsAnim]      = useState(false);
  const [viewerUrl,   setViewerUrl]   = useState<string | null>(null);
  const [viewerType,  setViewerType]  = useState<'image' | 'video'>('image');

  const handleLike = async () => {
    if (isAnim) return;
    setIsAnim(true);
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    const next = !liked;
    setLiked(next); setLikesCount(c => next ? c + 1 : c - 1);
    try {
      const session = await getValidToken();
      if (!session) throw new Error();
      const res  = await fetch(HOME_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: next ? 'like-post' : 'unlike-post', user_id: session.uid, access_token: session.token, post_id: post.id }),
      });
      const data = await res.json();
      if (data.success) setLikesCount(data.likes);
    } catch { setLiked(!next); setLikesCount(c => next ? c - 1 : c + 1); }
    finally { setTimeout(() => setIsAnim(false), 300); }
  };

  const handleShare = async (postId: string, sh: boolean) => {
    setShared(sh); setSharesCount(c => sh ? c + 1 : c - 1);
    try {
      const session = await getValidToken();
      if (!session) throw new Error();
      const res  = await fetch(HOME_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: sh ? 'share-post' : 'unshare-post', user_id: session.uid, access_token: session.token, post_id: postId }),
      });
      const data = await res.json();
      if (data.success) setSharesCount(data.shares);
    } catch { setShared(!sh); setSharesCount(c => sh ? c - 1 : c + 1); }
  };

  return (
    <>
      <MediaViewer visible={!!viewerUrl} url={viewerUrl} type={viewerType} onClose={() => setViewerUrl(null)} />

      <View style={S.postCard}>
        {/* HEADER */}
        <View style={S.postHeader}>
          <View style={S.postAuthor}>
            <SafeImage
              uri={post.author.avatar_url}
              style={S.postAvatar}
              resizeMode="cover"
              fallback={
                <View style={S.avatarPlaceholder}>
                  <Text style={S.avatarText}>{ini(post.author.nom, post.author.prenom)}</Text>
                </View>
              }
            />
            <View>
              <Text style={S.authorName}>{post.author.prenom} {post.author.nom}</Text>
              <Text style={S.postTime}>{fmtAgo(post.created_at, t)}</Text>
            </View>
          </View>
          <View style={S.postBadge}>
            <Text style={S.postBadgeTxt}>📝</Text>
          </View>
        </View>

        {/* CONTENU */}
        <Text style={S.postContent}><Hl text={post.content} q={q} /></Text>

        {/* IMAGE — thumbnail statique → MediaViewer */}
        {post.imagepots ? (
          <TouchableOpacity activeOpacity={0.92} onPress={() => { setViewerType('image'); setViewerUrl(post.imagepots!); }}>
            <SafeImage uri={post.imagepots} style={S.postImage} resizeMode="cover" />
            <View style={S.mediaExpandHint}>
              <Ionicons name="expand-outline" size={16} color="rgba(255,255,255,0.85)" />
            </View>
          </TouchableOpacity>
        ) : null}

        {/* VIDÉO — thumbnail statique → MediaViewer */}
        {post.vidposts ? (
          <TouchableOpacity
            style={S.videoThumb}
            activeOpacity={0.92}
            onPress={() => { setViewerType('video'); setViewerUrl(post.vidposts!); }}
          >
            <Ionicons name="play-circle" size={52} color="rgba(255,255,255,0.9)" />
            <Text style={S.videoLabel}>{t('searchModal.tapToPlay')}</Text>
          </TouchableOpacity>
        ) : null}

        {/* STATS BAR — cercle violet identique à profil.tsx */}
        <View style={S.statsBar}>
          {likesCount > 0 ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <View style={S.statsLikeCircle}>
                <MaterialCommunityIcons name="thumb-up" size={10} color="#FFF" />
              </View>
              <Text style={S.statsText}>{likesCount}</Text>
            </View>
          ) : <Text style={S.statsText}></Text>}
          <View style={S.statsRight}>
            {post.reactions.comments > 0 && <Text style={S.statsText}>{t('searchModal.commentsCount', { count: post.reactions.comments })}</Text>}
            {post.reactions.comments > 0 && sharesCount > 0 && <Text style={S.statsSep}>•</Text>}
            {sharesCount > 0 && <Text style={S.statsText}>{t('searchModal.sharesCount', { count: sharesCount })}</Text>}
          </View>
        </View>

        {/* ACTIONS BAR — identique à profil.tsx */}
        <View style={S.actionsBar}>
          <LikeButton liked={liked} isAnim={isAnim} onPress={handleLike} />

          <TouchableOpacity
            style={S.actionBtnModern}
            onPress={() => {
              if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              onComment();
            }}
            activeOpacity={0.6}
          >
            <View style={S.actionContentModern}>
              <Ionicons name="chatbubble-outline" size={22} color="#65676B" />
            </View>
          </TouchableOpacity>

          {(post.visibility === 'public' || post.visibility === 'pro') && (
            <ShareButton
              postId={post.id}
              content={post.content}
              isPro={post.visibility === 'pro'}
              shared={shared}
              sharesCount={sharesCount}
              onShareHarmonia={handleShare}
            />
          )}
        </View>
      </View>
    </>
  );
}

// ─── SubCard ──────────────────────────────────────────────────────────────────
function SubCard({ sub, q, t }: { sub: Submission; q: string; t: (key: string, opts?: any) => string }) {
  const [votes,    setVotes]    = useState(sub.total_votes);
  const [voted,    setVoted]    = useState(sub.user_voted);
  const [voting,   setVoting]   = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [viewerUrl,  setViewerUrl]  = useState<string | null>(null);
  const [viewerType, setViewerType] = useState<'image' | 'video'>('image');

  const isMusic = sub.game_type === 'music';
  const isImage = sub.game_type === 'arts';
  const badgeBg  = isImage ? '#F0E6FF' : isMusic ? '#EDE9FE' : '#FFF3E0';
  const badgeClr = isImage ? '#8E44AD' : isMusic ? '#7B1FA2' : '#E65100';

  const stMap: Record<string, { label: string; color: string }> = {
    submissions_open: { label: t('searchModal.status.submissions'), color: '#0EA5E9' },
    voting_open:      { label: t('searchModal.status.votingOpen'),  color: '#10B981' },
    finished:         { label: t('searchModal.status.finished'),    color: '#6B7280' },
  };
  const st = stMap[sub.run_status] ?? { label: sub.run_status, color: '#999' };

  const doVote = async () => {
    if (voting) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setVoting(true);
    const nv = !voted; setVoted(nv); setVotes(v => nv ? v + 1 : v - 1);
    try {
      const session = await getValidToken();
      if (!session) throw new Error();
      const res = await fetch(`${BACKEND_URL}/feed`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          function:         nv ? 'voteSubmission' : 'unvoteSubmission',
          user_id:          session.uid,
          access_token:     session.token,
          refresh_token:    session.refresh_token,
          expires_at:       session.expires_at,
          game_type:        sub.game_type,
          run_id:           sub.run_id,
          vote_for_user_id: sub.user_id,
          session_id:       sub.session_id,
        }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error);
      if (d.new_token) {
        const raw = await AsyncStorage.getItem('harmonia_session');
        if (raw) await AsyncStorage.setItem('harmonia_session', JSON.stringify({ ...JSON.parse(raw), ...d.new_token }));
      }
    } catch { setVoted(!nv); setVotes(v => nv ? v - 1 : v + 1); }
    finally { setVoting(false); }
  };

  const matchedDesc = sub.media.find(m => m.description?.toLowerCase().includes(q.toLowerCase()))?.description;
  const mediaLabel  = isImage ? t('searchModal.media.image') : isMusic ? t('searchModal.media.track') : t('searchModal.media.video');
  const mediaIcon   = isImage ? 'images-outline' : isMusic ? 'musical-notes-outline' : 'videocam-outline';

  return (
    <>
      <MediaViewer visible={!!viewerUrl} url={viewerUrl} type={viewerType} onClose={() => setViewerUrl(null)} />

      <View style={S.postCard}>
        {/* HEADER */}
        <View style={S.postHeader}>
          <View style={S.postAuthor}>
            <SafeImage
              uri={sub.author.avatar_url}
              style={S.postAvatar}
              resizeMode="cover"
              fallback={
                <View style={[S.avatarPlaceholder, { backgroundColor: badgeClr }]}>
                  <Text style={S.avatarText}>{ini(sub.author.nom, sub.author.prenom)}</Text>
                </View>
              }
            />
            <View>
              <Text style={S.authorName}>{sub.author.prenom} {sub.author.nom}</Text>
              <Text style={S.postTime}>{t('searchModal.runNumber', { number: sub.run_number })} — <Hl text={sub.run_title} q={q} /></Text>
            </View>
          </View>
          <View style={[S.gameBadge, { backgroundColor: badgeBg }]}>
            <Text style={[S.gameBadgeTxt, { color: badgeClr }]}>{sub.badge}</Text>
          </View>
        </View>

        {matchedDesc && (
          <View style={S.descRow}>
            <Ionicons name="chatbubble-outline" size={13} color="#999" />
            <Text style={S.descTxt} numberOfLines={2}><Hl text={matchedDesc} q={q} /></Text>
          </View>
        )}

        {/* MÉDIAS — thumbnail statique → MediaViewer */}
        {sub.media.length > 0 && (
          <>
            <TouchableOpacity style={S.mediaToggle} onPress={() => {
              if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              setExpanded(e => !e);
            }}>
              <Ionicons name={mediaIcon as any} size={15} color="#8A2BE2" />
              <Text style={S.mediaToggleTxt}>
                {expanded ? t('searchModal.media.hide') : t('searchModal.media.viewCount', { count: sub.media.length, label: mediaLabel })}
              </Text>
              <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={14} color="#8A2BE2" />
            </TouchableOpacity>

            {expanded && (
              <View style={S.mediaList}>
                {sub.media.map(m => {
                  if (isImage) {
                    return (
                      <TouchableOpacity
                        key={m.id}
                        activeOpacity={0.92}
                        onPress={() => { setViewerType('image'); setViewerUrl(m.url); }}
                      >
                        <SafeImage uri={m.url} style={S.postImage} resizeMode="cover" />
                        <View style={S.mediaExpandHint}>
                          <Ionicons name="expand-outline" size={16} color="rgba(255,255,255,0.85)" />
                        </View>
                      </TouchableOpacity>
                    );
                  }
                  // Musique ou vidéo → thumbnail statique → MediaViewer
                  return (
                    <TouchableOpacity
                      key={m.id}
                      style={S.videoThumb}
                      activeOpacity={0.92}
                      onPress={() => {
                        setViewerType('video');
                        setViewerUrl(m.url);
                      }}
                    >
                      <Ionicons
                        name={isMusic ? 'musical-notes' : 'play-circle'}
                        size={52}
                        color="rgba(255,255,255,0.9)"
                      />
                      {m.description && (
                        <Text style={S.videoLabel} numberOfLines={1}>{m.description}</Text>
                      )}
                      <Text style={[S.videoLabel, { fontSize: 11, opacity: 0.7 }]}>{t('searchModal.tapToPlay')}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
          </>
        )}

        {/* FOOTER vote */}
        <View style={S.subFooter}>
          <View style={[S.statusPill, { backgroundColor: st.color + '22' }]}>
            <Text style={[S.statusTxt, { color: st.color }]}>{st.label}</Text>
          </View>
          <View style={{ flex: 1 }} />
          {sub.run_status === 'voting_open' ? (
            <TouchableOpacity
              style={[S.voteBtn, voted && S.voteBtnVoted]}
              onPress={doVote}
              disabled={voting}
              activeOpacity={0.8}
            >
              {voting
                ? <ActivityIndicator size="small" color="#FFF" />
                : <>
                    <Ionicons name={voted ? 'heart' : 'heart-outline'} size={16} color="#FFF" />
                    <Text style={S.voteTxt}>{voted ? t('searchModal.voted') : t('searchModal.vote')}</Text>
                    <Text style={S.voteCount}>{votes}</Text>
                  </>}
            </TouchableOpacity>
          ) : (
            <View style={S.voteStat}>
              <Ionicons name="heart" size={14} color="#E74C3C" />
              <Text style={S.voteStatTxt}>{t('searchModal.votesCount', { count: votes })}</Text>
            </View>
          )}
        </View>
      </View>
    </>
  );
}

// ─── SearchModal principal ────────────────────────────────────────────────────
export default function SearchModal({ visible, onClose, onPostPress }: SearchModalProps) {
  const { t } = useTranslation();
  const CATS = useCats(t);
  const [query,    setQuery]    = useState('');
  const [cat,      setCat]      = useState<SearchCategory>('all');
  const [results,  setResults]  = useState<Result[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);
  const [userId,   setUserId]   = useState('');

  useEffect(() => {
    if (!visible) { setQuery(''); setResults([]); setSearched(false); setCat('all'); }
    else {
      AsyncStorage.getItem('harmonia_session').then(raw => {
        if (raw) setUserId(JSON.parse(raw)?.user?.id || '');
      });
    }
  }, [visible]);

  const fetchPosts = async (): Promise<Post[]> => {
    const session = await getValidToken(); if (!session) return [];
    const res = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'search-posts', user_id: session.uid, access_token: session.token, query: query.trim() }),
    });
    const d = await res.json();
    return d.success && d.posts ? d.posts : [];
  };

  const fetchSubs = async (): Promise<Submission[]> => {
    const session  = await getValidToken(); if (!session) return [];
    const gameType = GT_FOR_CAT[cat];
    const body: any = {
      function: 'searchSubmissions', user_id: session.uid, access_token: session.token,
      refresh_token: session.refresh_token, expires_at: session.expires_at, query: query.trim(),
    };
    if (gameType) body.game_type = gameType;
    const res = await fetch(`${BACKEND_URL}/feed`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const d = await res.json();
    if (d.new_token) {
      const raw = await AsyncStorage.getItem('harmonia_session');
      if (raw) await AsyncStorage.setItem('harmonia_session', JSON.stringify({ ...JSON.parse(raw), ...d.new_token }));
    }
    return d.success && d.submissions ? d.submissions : [];
  };

  const doSearch = async () => {
    if (!query.trim()) return;
    Keyboard.dismiss();
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setLoading(true); setSearched(true);
    try {
      const searchPosts = cat === 'all' || cat === 'posts';
      const searchSubs  = cat === 'all' || cat !== 'posts';
      const [posts, subs] = await Promise.all([
        searchPosts ? fetchPosts() : Promise.resolve([]),
        searchSubs  ? fetchSubs()  : Promise.resolve([]),
      ]);
      const merged: Result[] = [
        ...posts.map(p => ({ ...p, kind: 'post' as const })),
        ...subs.map(s  => ({ ...s, kind: 'sub'  as const })),
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setResults(merged);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  const postsN = results.filter(r => r.kind === 'post').length;
  const subsN  = results.filter(r => r.kind === 'sub').length;

  return (
    <Modal visible={visible} animationType="slide" transparent={false} onRequestClose={onClose}>
      <View style={S.root}>

        {/* HEADER GRADIENT */}
        <LinearGradient colors={['#8A2BE2', '#4B0082']} style={S.header}>
          <View style={S.headerTop}>
            <TouchableOpacity onPress={onClose} style={S.backBtn}>
              <Ionicons name="chevron-back" size={26} color="#FFF" />
            </TouchableOpacity>
            <Text style={S.headerTitle}>{t('searchModal.header.title')}</Text>
            <View style={{ width: 34 }} />
          </View>

          {/* BARRE DE RECHERCHE */}
          <View style={S.searchBar}>
            <Ionicons name="search" size={20} color="#999" style={{ marginRight: 8 }} />
            <TextInput
              style={S.searchInput}
              placeholder={t('searchModal.placeholder')}
              placeholderTextColor="#999"
              value={query}
              onChangeText={setQuery}
              onSubmitEditing={doSearch}
              returnKeyType="search"
              autoFocus
            />
            {query.length > 0 && (
              <TouchableOpacity onPress={() => { setQuery(''); setResults([]); setSearched(false); }}>
                <Ionicons name="close-circle" size={20} color="#999" />
              </TouchableOpacity>
            )}
          </View>

          {/* CATÉGORIES */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={S.chips} style={{ marginBottom: 10 }}>
            {CATS.map(c => {
              const active = cat === c.key;
              return (
                <TouchableOpacity
                  key={c.key}
                  style={[S.chip, active && S.chipOn]}
                  onPress={() => {
                    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                    setCat(c.key); setResults([]); setSearched(false);
                  }}
                >
                  <Ionicons name={c.icon as any} size={13} color={active ? '#8A2BE2' : 'rgba(255,255,255,0.8)'} />
                  <Text style={[S.chipTxt, active && S.chipTxtOn]}>{c.label}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          {/* BOUTON RECHERCHER */}
          <TouchableOpacity
            style={[S.searchBtn, (!query.trim() || loading) && { opacity: 0.4 }]}
            onPress={doSearch}
            disabled={!query.trim() || loading}
          >
            {loading
              ? <ActivityIndicator size="small" color="#FFF" />
              : <><Ionicons name="search" size={18} color="#FFF" /><Text style={S.searchBtnTxt}>{t('searchModal.searchButton')}</Text></>}
          </TouchableOpacity>
        </LinearGradient>

        {/* RÉSULTATS */}
        <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
          {loading ? (
            <View style={S.center}>
              <ActivityIndicator size="large" color="#8A2BE2" />
              <Text style={S.stTxt}>{t('searchModal.searching')}</Text>
            </View>
          ) : searched && results.length === 0 ? (
            <View style={S.center}>
              <Ionicons name="search-outline" size={70} color="#CCC" />
              <Text style={S.stTitle}>{t('searchModal.noResults.title')}</Text>
              <Text style={S.stTxt}>{t('searchModal.noResults.subtitle')}</Text>
            </View>
          ) : searched ? (
            <>
              <View style={S.countBar}>
                <Text style={S.countTxt}>{t('searchModal.resultsCount', { count: results.length })}</Text>
                <View style={{ flexDirection: 'row', gap: 6 }}>
                  {postsN > 0 && <View style={S.pill}><Text style={S.pillTxt}>📝 {postsN}</Text></View>}
                  {subsN  > 0 && <View style={S.pill}><Text style={S.pillTxt}>🎭 {subsN}</Text></View>}
                </View>
              </View>
              {results.map(item =>
                item.kind === 'post'
                  ? <PostCard key={`p-${item.id}`} post={item} q={query} userId={userId} onComment={() => { onClose(); onPostPress(item.id); }} t={t} />
                  : <SubCard  key={`s-${item.id}`} sub={item}  q={query} t={t} />
              )}
              <View style={{ height: 50 }} />
            </>
          ) : (
            <View style={S.center}>
              <Ionicons name="search-circle-outline" size={80} color="#E0D0FF" />
              <Text style={S.stTitle}>{t('searchModal.intro.title')}</Text>
              <Text style={S.stTxt}>{t('searchModal.intro.subtitle')}</Text>
            </View>
          )}
        </ScrollView>
      </View>
    </Modal>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  root:        { flex: 1, backgroundColor: '#F5F5F5' },

  // Header
  header:      { paddingTop: Platform.OS === 'ios' ? 55 : 40, paddingBottom: 6, paddingHorizontal: 16 },
  headerTop:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  backBtn:     { padding: 4 },
  headerTitle: { fontSize: 18, fontWeight: 'bold', color: '#FFF' },

  searchBar:   { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF', borderRadius: 12, paddingHorizontal: 12, height: 44, marginBottom: 12 },
  searchInput: { flex: 1, fontSize: 15, color: '#333' },
  chips:       { paddingRight: 16, gap: 8, flexDirection: 'row' },
  chip:        { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 6 },
  chipOn:      { backgroundColor: '#FFF' },
  chipTxt:     { fontSize: 12, fontWeight: '600', color: 'rgba(255,255,255,0.85)' },
  chipTxtOn:   { color: '#8A2BE2' },
  searchBtn:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.25)', paddingVertical: 10, borderRadius: 10, gap: 6, marginTop: 4, marginBottom: 6 },
  searchBtnTxt: { color: '#FFF', fontSize: 15, fontWeight: '600' },

  // États vides
  center:   { paddingVertical: 70, alignItems: 'center', paddingHorizontal: 40 },
  stTitle:  { fontSize: 17, fontWeight: '700', color: '#999', marginTop: 18, textAlign: 'center' },
  stTxt:    { fontSize: 13, color: '#BBB', marginTop: 8, textAlign: 'center', lineHeight: 20 },
  countBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#F0F0F0' },
  countTxt: { fontSize: 13, fontWeight: '700', color: '#8A2BE2' },
  pill:     { backgroundColor: '#F0E6FF', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 },
  pillTxt:  { fontSize: 11, fontWeight: '700', color: '#8A2BE2' },

  // PostCard — identique à profil.tsx
  postCard:          { backgroundColor: '#fff', marginHorizontal: 12, marginVertical: 6, borderRadius: 14, paddingVertical: 14 },
  postHeader:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, marginBottom: 10 },
  postAuthor:        { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 10 },
  postAvatar:        { width: 40, height: 40, borderRadius: 20 },
  avatarPlaceholder: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  avatarText:        { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  authorName:        { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  postTime:          { fontSize: 12, color: '#999', marginTop: 1 },
  postBadge:         { backgroundColor: '#F5F5F5', borderRadius: 6, paddingHorizontal: 7, paddingVertical: 3 },
  postBadgeTxt:      { fontSize: 12 },
  postContent:       { fontSize: 14, color: '#333', lineHeight: 20, paddingHorizontal: 14, marginBottom: 10 },

  // Médias
  postImage:       { width: '100%', height: 260, marginBottom: 10 },
  videoThumb:      { width: '100%', height: 180, backgroundColor: '#1A1A2E', justifyContent: 'center', alignItems: 'center', marginBottom: 10, gap: 8 },
  videoLabel:      { color: 'rgba(255,255,255,0.75)', fontSize: 13 },
  mediaExpandHint: { position: 'absolute', bottom: 18, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: 4 },

  // Stats bar — cercle violet identique à profil.tsx
  statsBar:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10 },
  statsLikeCircle: { backgroundColor: '#7B1FA2', width: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center' },
  statsText:       { fontSize: 13, color: '#65676B' },
  statsRight:      { flexDirection: 'row', alignItems: 'center', gap: 5 },
  statsSep:        { color: '#CCC' },

  // Actions bar — identique à profil.tsx
  actionsBar:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly', borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#CED0D4', paddingTop: 4, paddingBottom: 4, marginHorizontal: 12, marginTop: 4 },
  actionBtnModern:     { flex: 1, paddingVertical: 8, borderRadius: 8 },
  actionContentModern: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  likeBtnInner:        { borderRadius: 8, paddingVertical: 4, paddingHorizontal: 8 },
  likeBtnActive:       { shadowColor: '#7B1FA2', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.35, shadowRadius: 8, elevation: 4 },

  // Highlight texte
  hl: { backgroundColor: '#FFF3CD', fontWeight: '700', color: '#8A2BE2' },

  // SubCard spécifique
  gameBadge:    { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  gameBadgeTxt: { fontSize: 11, fontWeight: '700' },
  descRow:      { flexDirection: 'row', alignItems: 'flex-start', gap: 6, marginBottom: 10, backgroundColor: '#FAFAFA', borderRadius: 8, padding: 8, marginHorizontal: 14 },
  descTxt:      { flex: 1, fontSize: 13, color: '#555', lineHeight: 18 },
  mediaToggle:    { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8, paddingHorizontal: 14 },
  mediaToggleTxt: { flex: 1, fontSize: 13, color: '#8A2BE2', fontWeight: '600' },
  mediaList:      { gap: 10, marginBottom: 10, paddingHorizontal: 14 },

  // Sub footer vote
  subFooter:   { flexDirection: 'row', alignItems: 'center', paddingTop: 10, paddingHorizontal: 14, borderTopWidth: 1, borderTopColor: '#F5F5F5', gap: 8, marginTop: 4 },
  statusPill:  { borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 },
  statusTxt:   { fontSize: 11, fontWeight: '700' },
  voteBtn:     { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#8A2BE2', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 7 },
  voteBtnVoted:{ backgroundColor: '#E74C3C' },
  voteTxt:     { color: '#FFF', fontSize: 13, fontWeight: '700' },
  voteCount:   { color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: '600' },
  voteStat:    { flexDirection: 'row', alignItems: 'center', gap: 4 },
  voteStatTxt: { fontSize: 13, color: '#E74C3C', fontWeight: '700' },

  // MediaViewer
  mvOverlay:     { flex: 1, backgroundColor: 'rgba(0,0,0,0.97)', justifyContent: 'center', alignItems: 'center' },
  mvCloseBtn:    { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, right: 20, zIndex: 10, padding: 8, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20 },
  mvImage:       { width, height: height * 0.85 },
  mvNativeVideo: { width, height: height * 0.75 },
});
